import { useSupabaseCallback, useSupabaseLoader } from "../../../server/supabase/hooks";
import { SupabasePersonDetailsService } from "../../employees/server/supabase-person-service";
import { AccessIndex } from "../access-model";

import type { UseSupabaseLoaderState } from "../../../server/supabase/hooks/types";
import type { UUID } from "../../../utils/uuid";
import type { Level } from "../access-model";

export interface AccessPerson {
  id: UUID;
  name: string;
  email: string;
  initials: string;
}

export interface AccessData {
  admins: AccessPerson[];
  contributors: AccessPerson[];
  /** Each person who is not an admin or a reviewer. The first access setting makes one a contributor. */
  people: AccessPerson[];
  index: AccessIndex;
}

const byName = (a: AccessPerson, b: AccessPerson): number => a.name.localeCompare(b.name);

/** The admins, the contributors and each contributor's program access, for the Access dialog. */
export const useProgramAccess = (): UseSupabaseLoaderState<AccessData> & { reload: () => void } =>
  useSupabaseLoader(async ({ supabase, account_id }) => {
    const [permissions, access, people] = await Promise.all([
      supabase
        .from("permissions")
        .select("id, email, super_admin, reviewer")
        .eq("account_id", account_id),
      supabase.from("contributor_program_access").select("*"),
      new SupabasePersonDetailsService(supabase).getAll(account_id, {
        order: [{ column: "id" }],
      }),
    ]);
    if (permissions.error) throw permissions.error;
    if (access.error) throw access.error;
    if (people.error) throw people.error;

    const details = new Map(people.data.map((person) => [person.id, person]));
    const toPerson = ({ id, email }: { id: UUID; email: string | null }): AccessPerson => {
      const person = details.get(id);
      const name = person ? `${person.first_name} ${person.last_name}`.trim() : "";
      return {
        id,
        name: name || email || "",
        email: person?.email ?? email ?? "",
        initials: person
          ? `${person.first_name.charAt(0)}${person.last_name.charAt(0)}`.toUpperCase()
          : (email ?? "?").charAt(0).toUpperCase(),
      };
    };
    const rows = permissions.data.flatMap(({ id, email, super_admin, reviewer }) =>
      id ? [{ id, email, super_admin, reviewer }] : [],
    );
    const notContributors = new Set(
      rows.filter(({ super_admin, reviewer }) => super_admin || reviewer).map(({ id }) => id),
    );

    return {
      admins: rows
        .filter(({ super_admin }) => super_admin)
        .map(toPerson)
        .toSorted(byName),
      contributors: rows
        .filter(({ super_admin, reviewer }) => !super_admin && !reviewer)
        .map(toPerson)
        .toSorted(byName),
      people: people.data
        .flatMap(({ id, email }) => (id && !notContributors.has(id) ? [{ id, email }] : []))
        .map(toPerson)
        .toSorted(byName),
      index: new AccessIndex(access.data),
    };
  }, []);

/** Sets one contributor's level on one program. The db removes a level that is the same as above. */
export const useSetProgramAccess = (): ((
  personId: UUID,
  programId: UUID,
  level: Level,
) => Promise<void>) => {
  const call = useSupabaseCallback(
    async ({ supabase }, person_id_: UUID, program_id_: UUID, level_: Level) => {
      const { error } = await supabase.rpc("set_program_access", {
        person_id_,
        program_id_,
        level_,
      });
      if (error) throw error;
    },
    [],
  );

  return async (personId, programId, level) => {
    await call(personId, programId, level);
  };
};

/** Removes one contributor's settings on a program and on each program below it. */
export const useRemoveProgramAccess = (): ((personId: UUID, programId: UUID) => Promise<void>) => {
  const call = useSupabaseCallback(async ({ supabase }, person_id_: UUID, program_id_: UUID) => {
    const { error } = await supabase.rpc("remove_program_access", { person_id_, program_id_ });
    if (error) throw error;
  }, []);

  return async (personId, programId) => {
    await call(personId, programId);
  };
};
